import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarDays, Play, BookOpen, FolderOpen, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import es from 'date-fns/locale/es'
import FeaturedLayout from '../layouts/FeaturedLayout'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '../ui'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { getMexicoDateString } from '../../lib/dateUtils'
import {
  generateTrainingFileUrl,
  getAllGroups,
  getCurrentStaff,
  getStaffGroups,
  getTrainingProgramAgenda
} from '../../lib/supabase'

const getToday = () => getMexicoDateString()

const TrainingTodayProgram = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { isAdmin, isHeadCoach } = usePermissions()

  const [groups, setGroups] = useState([])
  const [agendaItems, setAgendaItems] = useState([])
  const [loading, setLoading] = useState(true)

  const loadGroups = useCallback(async () => {
    if (!user) return
    try {
      if (isAdmin || isHeadCoach) {
        const { data, error } = await getAllGroups()
        if (error) throw error
        setGroups(data || [])
        return
      }
      const { data: staffData, error: staffError } = await getCurrentStaff()
      if (staffError) throw staffError
      const staffId = staffData?.id
      if (!staffId) {
        setGroups([])
        return
      }
      const { data, error } = await getStaffGroups(staffId)
      if (error) throw error
      const mapped = (data || []).map((item) => item.grupos).filter(Boolean)
      setGroups(mapped)
    } catch (error) {
      console.error('Error cargando grupos:', error)
      setGroups([])
    }
  }, [user, isAdmin, isHeadCoach])

  const loadAgendaToday = useCallback(async () => {
    setLoading(true)
    try {
      const today = getToday()
      const { data, error } = await getTrainingProgramAgenda({
        startDate: today,
        endDate: today,
        groupId: null
      })
      if (error) throw error
      setAgendaItems(data || [])
    } catch (error) {
      console.error('Error cargando programa de hoy:', error)
      setAgendaItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadGroups()
    loadAgendaToday()
  }, [loadGroups, loadAgendaToday])

  const accessibleGroupIds = useMemo(() => groups.map((group) => group.id), [groups])
  const canAccessAll = isAdmin || isHeadCoach

  const itemsByProgram = useMemo(() => {
    const map = new Map()
    agendaItems.forEach((item) => {
      // Admin/head_coach ven todo; para coach se filtra por sus grupos (sin
      // descartar items mientras los grupos aun cargan: solo filtra si ya hay grupos).
      if (!canAccessAll && accessibleGroupIds.length && item.grupo_id && !accessibleGroupIds.includes(item.grupo_id)) return
      const programId = item.programa?.id || item.programa_id || item.id
      if (!programId) return
      if (!map.has(programId)) {
        map.set(programId, {
          programa: item.programa,
          items: []
        })
      }
      map.get(programId).items.push(item)
    })
    return Array.from(map.values())
  }, [agendaItems, accessibleGroupIds, canAccessAll])

  const handleOpenProgramFile = async (file) => {
    if (!file) return
    try {
      if (file.storage_path) {
        const { data, error } = await generateTrainingFileUrl(file.storage_path, 600)
        if (error) throw error
        window.open(data.signedUrl, '_blank')
      } else if (file.file_url) {
        window.open(file.file_url, '_blank')
      }
    } catch (error) {
      console.error('Error abriendo archivo del programa:', error)
    }
  }

  return (
    <FeaturedLayout
      title="Programa de hoy"
      subtitle="Consulta el programa asignado para el entrenamiento de hoy."
    >
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarDays className="h-4 w-4" />
            <span>{format(new Date(), "EEEE d 'de' MMMM yyyy", { locale: es })}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/groups')}
              className="flex items-center gap-2"
            >
              <Play className="h-4 w-4" />
              Entrenar hoy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/programas/agenda')}
              className="flex items-center gap-2"
            >
              <BookOpen className="h-4 w-4" />
              Programas de entrenamiento
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Cargando programa de hoy...
          </div>
        ) : itemsByProgram.length === 0 ? (
          <Card className="border shadow-none">
            <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
              <p className="text-sm text-muted-foreground">
                No hay programas asignados para hoy.
              </p>
              {(isAdmin || isHeadCoach) ? (
                <Button size="sm" onClick={() => navigate('/programas/biblioteca')}>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Asignar un programa
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Tu administrador o head coach puede asignar uno desde la Biblioteca de programas.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {itemsByProgram.map(({ programa, items }) => {
              const groupNames = items
                .map((registro) => registro.grupo?.nombre_del_grupo)
                .filter(Boolean)
              const uniqueGroups = Array.from(new Set(groupNames))
              const hasGeneral = items.some((registro) => !registro.grupo?.nombre_del_grupo)
              const primaryFile = programa?.archivos?.[0]
              return (
              <Card key={programa?.id || 'programa'} className="border shadow-none">
                <CardHeader className="pb-2 flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold">
                      {programa?.titulo || 'Programa sin título'}
                    </CardTitle>
                    {programa?.secciones && programa.secciones.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        ⏱ {Math.floor(programa.secciones.reduce((s, x) => s + (x.duracion_seg || 0), 0) / 60)} min · {programa.secciones.length} secciones
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {uniqueGroups.length || hasGeneral
                        ? `${uniqueGroups.length + (hasGeneral ? 1 : 0)} grupo(s) asignado(s)`
                        : 'Sin grupo definido'}
                    </p>
                  </div>
                  {primaryFile && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenProgramFile(primaryFile)}
                    >
                      <FolderOpen className="h-4 w-4 mr-2" />
                      Abrir
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {programa?.descripcion && (
                    <p className="text-sm text-muted-foreground">{programa.descripcion}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {(programa?.deportes || []).map((sport) => (
                      <Badge key={sport} variant="outline">
                        {sport}
                      </Badge>
                    ))}
                    {(programa?.tags || []).slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {uniqueGroups.map((name) => (
                      <Badge key={name} variant="outline" className="text-[11px]">
                        {name}
                      </Badge>
                    ))}
                    {hasGeneral && (
                      <Badge variant="secondary" className="text-[11px]">
                        General
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {uniqueGroups.map((name) => {
                      const group = groups.find((g) => g.nombre_del_grupo === name)
                      if (!group) return null
                      return (
                        <Button
                          key={group.id}
                          size="sm"
                          className="flex items-center gap-2"
                          onClick={() =>
                            navigate(`/attendance/${group.id}`, {
                              state: {
                                group,
                                mode: 'entrenamiento',
                                fecha: getToday()
                              }
                            })
                          }
                        >
                          <Play className="h-4 w-4" />
                          Entrenar {group.nombre_del_grupo}
                        </Button>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            )
            })}
          </div>
        )}
      </div>
    </FeaturedLayout>
  )
}

export default TrainingTodayProgram
